import { defineComponent as _defineComponent } from 'vue'
import { onMounted, ref, computed, watch } from 'vue';
import { Company, ProjectStatus, Customer, Contact, CatalogType } from '@newmoon-org/types';
import { useRouter } from 'vue-router/composables';
import { NotificationProgrammatic } from 'buefy';

import ProjectService from '@/service/project.service';
import workordersService from '@/service/workorders.service';
import catalogTypeService from '@/service/catalogType.service';
import workOrdersService from '@/service/workorders.service';
import { create as createJobSite } from '@/service/jobSite.service';
import TaxService from '@/service/tax.service';

import CustomerAutocomplete from '@/components/customers/CustomerAutocomplete.vue';
import JobsiteAutocomplete from '@/components/jobsites/JobsiteAutocomplete.vue';
import JobSiteModal from '@/components/work/JobSiteModal.vue';

import type { JobSite } from '@/types/JobSite';

type BasicProject = {
  code: string;
  name: string;
  customer: {
    name: string;
  };
  startDate?: Date;
  endDate?: Date;
  company: string;
  status: ProjectStatus;
  mlhBudget: number;
  deleted: false;
  billingContact?: Contact;
  catalog: {
    id: string;
    name: string;
  };
  class: {
    id: string;
    name: string;
  };
  jobSite?: JobSite;
  taxJurisdictionCode?: string | null;
  taxJurisdictionTaxRate?: number | null;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ProjectCreatePage',
  setup(__props) {

const router = useRouter();

const project = ref<BasicProject>({
  code: '',
  name: '',
  customer: {
    name: '',
  },
  startDate: undefined,
  endDate: undefined,
  company: '',
  status: ProjectStatus.Pending,
  mlhBudget: 0,
  deleted: false,
  billingContact: undefined,
  catalog: {
    id: '',
    name: '',
  },
  class: {
    id: '',
    name: '',
  },
  taxJurisdictionCode: null,
  taxJurisdictionTaxRate: null,
});

const isLoading = ref<boolean>(false);
const companies = ref<Company[]>([]);
const catalogs = ref<CatalogType[]>([]);
const reasons = ref<any[]>([]);
const selectedCustomer = ref<Customer | null>(null);
const selectedBillingContact = ref<Contact | null>(null);
const selectedCatalog = ref<CatalogType | null>(null);
const selectedClass = ref<any | null>(null);
const selectedJobSiteAddress = ref<string>('');
const showJobSiteModal = ref<boolean>(false);

const customerContacts = computed(() => selectedCustomer.value?.contacts || []);

onMounted(async () => {
  companies.value = (await workordersService.projectCompanies()) as Company[];
  catalogs.value = await catalogTypeService.getAll();
  reasons.value = await workOrdersService.getReasons();
});

watch(selectedBillingContact, newContact => {
  if (newContact) {
    project.value.billingContact = newContact;
  }
});

watch(selectedCatalog, newCatalog => {
  if (newCatalog) {
    project.value.catalog = {
      id: newCatalog.id,
      name: newCatalog.name,
    };
  }
});

watch(selectedClass, newClass => {
  if (newClass) {
    project.value.class = {
      id: newClass.id,
      name: newClass.name,
    };
  }
});

function onCustomerSelected(customer: Customer) {
  project.value.customer = customer;
  selectedCustomer.value = customer;

  if (customer.contacts && customer.contacts.length > 0) {
    selectedBillingContact.value = customer.contacts[0];
    project.value.billingContact = customer.contacts[0];
  }

  project.value.jobSite = undefined;
  selectedJobSiteAddress.value = '';
  project.value.taxJurisdictionCode = null;
  project.value.taxJurisdictionTaxRate = null;
}

async function jobSiteChanged(jobSite: JobSite | null) {
  if (!jobSite) {
    project.value.jobSite = undefined;
    selectedJobSiteAddress.value = '';
    project.value.taxJurisdictionCode = null;
    project.value.taxJurisdictionTaxRate = null;
    return;
  }
  project.value.jobSite = {
    ...jobSite,
    id: jobSite.id,
  };
  selectedJobSiteAddress.value = jobSite.address || '';

  const address =
    jobSite.address?.trim() ||
    [jobSite.street, jobSite.city, jobSite.state, jobSite.zip].filter(Boolean).join(' ').trim();
  if (address) {
    try {
      const taxInfo = await TaxService.getTaxRate(address);
      project.value.taxJurisdictionCode = taxInfo.taxJurisdictionCode ?? null;
      project.value.taxJurisdictionTaxRate = taxInfo.taxRatePercentage != null ? taxInfo.taxRatePercentage / 100 : null;
    } catch (e) {
      console.error('Error fetching tax info:', e);
      project.value.taxJurisdictionCode = null;
      project.value.taxJurisdictionTaxRate = null;
    }
  } else {
    project.value.taxJurisdictionCode = null;
    project.value.taxJurisdictionTaxRate = null;
  }
}

async function onJobSiteSaved(jobSite: JobSite) {
  if (!selectedCustomer.value?.id) {
    return;
  }
  const newJobSite = await createJobSite({
    ...jobSite,
    customerId: selectedCustomer.value.id,
  });
  jobSiteChanged(newJobSite);
  showJobSiteModal.value = false;
}

async function create() {
  isLoading.value = true;
  try {
    if (selectedBillingContact.value) {
      project.value.billingContact = selectedBillingContact.value;
    }

    if (!project.value.jobSite?.id) {
      NotificationProgrammatic.open({
        message: 'Please select a job site for the project',
        type: 'is-danger',
        duration: 5000,
      });
      isLoading.value = false;
      return;
    }

    const createdProject = await ProjectService.create(project.value);
    router.push({
      name: 'project-edit',
      params: {
        projectId: createdProject.id,
      },
    });
  } finally {
    isLoading.value = false;
  }
}

function cancel() {
  router.push({ name: 'projects' });
}

return { __sfc: true,router, project, isLoading, companies, catalogs, reasons, selectedCustomer, selectedBillingContact, selectedCatalog, selectedClass, selectedJobSiteAddress, showJobSiteModal, customerContacts, onCustomerSelected, jobSiteChanged, onJobSiteSaved, create, cancel, CustomerAutocomplete, JobsiteAutocomplete, JobSiteModal }
}

})